import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import MaterialSymbol from './MaterialSymbol';
import accountRoleTreatment from './accountRoleTreatments';
import outlinedOnSurfaceSx from './outlinedOnSurfaceSx';
import { createHighContrastHoverSwapSx } from './highContrastSx';
import useRedesignAccount from '../services/useRedesignAccount';
import { useConnectedOperator } from '../context/ConnectedOperatorContext';
import { ROLE_ADMINISTRATOR } from '../services/readRedesignAccount';
import { CONTENT_MAX_WIDTH } from '../config/layout';

const CAPACITY_MESSAGE_KEYS = Object.freeze({
    [ROLE_ADMINISTRATOR]: 'connectedOperatorMessage',
});

const OPERATOR_MESSAGE_KEY = 'connectedOperatorMessageOperator';

function capacityMessage(translations, capacity) {
    return translations[CAPACITY_MESSAGE_KEYS[capacity] || OPERATOR_MESSAGE_KEY];
}

function buildBarSx(treatment) {
    return {
        bgcolor: treatment.bgcolor,
        color: treatment.color,
        width: '100%',
    };
}

const CONTENT_SX = theme => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    mx: 'auto',
    px: 2,
    py: 1,
    fontSize: theme.typography.pxToRem(16),
});

const ICON_SX = {
    flexShrink: 0,
};

const MESSAGE_SX = {
    flexGrow: 1,
    minWidth: 0,
};

const CHECKOUT_NOTE_SX = {
    mt: 0.25,
};

const IMPORTANT_LABEL_SX = {
    fontWeight: 700,
};

function buildLogoutButtonSx(treatment) {
    return [
        {
            ...outlinedOnSurfaceSx(treatment.badgeBackground),
            flexShrink: 0,
            whiteSpace: 'nowrap',
        },
        createHighContrastHoverSwapSx(treatment.color, treatment.bgcolor),
    ];
}

function ConnectedOperatorBanner({ translations, checkoutMode = false }) {
    const account = useRedesignAccount();
    const connectedOperator = useConnectedOperator();
    const theme = useTheme();

    if (!connectedOperator) {
        return null;
    }

    const treatment = accountRoleTreatment(account.operatorCapacity, theme);

    return (
        <Box
            role="alert"
            sx={buildBarSx(treatment)}
            data-testid="connected-operator-banner"
            data-capacity={account.operatorCapacity}
        >
            <Box sx={CONTENT_SX}>
                <MaterialSymbol name="warning" size={22} sx={ICON_SX} />
                <Box sx={MESSAGE_SX} data-testid="connected-operator-message">
                    {capacityMessage(translations, account.operatorCapacity)}
                    {checkoutMode && (
                        <Box sx={CHECKOUT_NOTE_SX}>
                            <Box component="strong" sx={IMPORTANT_LABEL_SX}>
                                {translations.connectedOperatorCheckoutImportant}
                            </Box>
                            {' '}
                            {translations.connectedOperatorCheckoutPaymentSkipped}
                        </Box>
                    )}
                </Box>
                {checkoutMode ? (
                    <Button
                        href={account.logoutUrl}
                        variant="outlined"
                        size="small"
                        startIcon={<MaterialSymbol name="logout" size={18} />}
                        data-testid="connected-operator-logout"
                        sx={buildLogoutButtonSx(treatment)}
                    >
                        {translations.logoutLabel}
                    </Button>
                ) : null}
            </Box>
        </Box>
    );
}

ConnectedOperatorBanner.propTypes = {
    translations: PropTypes.shape({
        connectedOperatorMessage: PropTypes.string.isRequired,
        connectedOperatorMessageOperator: PropTypes.string.isRequired,
        connectedOperatorCheckoutImportant: PropTypes.string.isRequired,
        connectedOperatorCheckoutPaymentSkipped: PropTypes.string.isRequired,
        logoutLabel: PropTypes.string.isRequired,
    }).isRequired,
    checkoutMode: PropTypes.bool,
};

export default ConnectedOperatorBanner;
